import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { KpiCard } from "../components/KpiCard";
import { api } from "../lib/api";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from "recharts";
import { ChevronRight, AlertTriangle, Clock, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "../lib/utils";

const MIX_COLORS = { auction: "#FF4343", trade: "#090E33", service_drive: "#3B82F6", private_party: "#94A3B8" };
const MIX_LABEL = { auction: "Auction", trade: "Trade", service_drive: "Service drive", private_party: "Private party" };

export default function CorporateOverview() {
  const [overview, setOverview] = useState(null);
  const [rooftops, setRooftops] = useState([]);
  const [scorecard, setScorecard] = useState([]);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    (async () => {
      const [o, r, s, a] = await Promise.all([
        api.get("/corporate/overview"),
        api.get("/rooftops"),
        api.get("/corporate/buyer-scorecard"),
        api.get("/alerts?limit=8&acknowledged=false"),
      ]);
      setOverview(o.data); setRooftops(r.data); setScorecard(s.data); setAlerts(a.data);
    })();
  }, []);

  if (!overview) return <Layout><div className="p-8 text-slate-500">Loading…</div></Layout>;

  const totalInvM = (overview.total_inventory_value / 1_000_000).toFixed(1);
  const pieData = overview.acquisition_mix.map((m) => ({ name: MIX_LABEL[m.source] || m.source, value: m.count, src: m.source, gross: m.avg_gross }));
  const coachFlags = scorecard.filter((b) => b.override_rate > 25 || b.strong_buy_hit_rate < 65).slice(0, 4);

  return (
    <Layout variant="corporate">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Corporate</div>
        <h1 className="text-3xl font-bold text-navy">Overview</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard label="Total inventory" testId="kpi-inventory"
          value={`${overview.total_inventory_units.toLocaleString()}`} sub={`$${totalInvM}M total MMR`} />
        <KpiCard label="Group days supply" testId="kpi-days-supply"
          value={`${overview.days_supply}d`} sub={`Target ${overview.days_supply_target}d`} trend={-3} />
        <KpiCard label="Aged 60+ days" testId="kpi-aged" tone={overview.aged_over_60_pct > 15 ? "alert" : "default"}
          value={`${overview.aged_over_60_pct}%`} sub={`${overview.aged_over_60_count} units`} />
        <KpiCard label="Strong buys captured" testId="kpi-strong-buys"
          value={`${overview.strong_buys_captured_this_week} / ${overview.strong_buys_available + overview.strong_buys_captured_this_week}`}
          sub="this week" trend={overview.strong_buys_captured_this_week > 5 ? 8 : -2} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
        <div className="xl:col-span-2 bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-navy">Rooftop leaderboard</h2>
            <Link to="/corporate/rooftops" className="text-xs font-semibold text-freddy-red hover:underline flex items-center gap-1">
              All rooftops <ChevronRight size={12} />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rooftop-leaderboard">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left py-2 font-semibold">Rooftop</th>
                  <th className="text-right py-2 font-semibold">Units 90d</th>
                  <th className="text-right py-2 font-semibold">Avg gross</th>
                  <th className="text-right py-2 font-semibold">Turn</th>
                  <th className="text-right py-2 font-semibold">CTM</th>
                  <th className="text-right py-2 font-semibold">Strong open</th>
                </tr>
              </thead>
              <tbody>
                {rooftops.slice().sort((a, b) => b.avg_front_gross - a.avg_front_gross).map((r) => (
                  <tr key={r.rooftop_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2.5">
                      <Link to={`/rooftop/${r.rooftop_id}`} className="font-semibold text-navy hover:text-freddy-red">
                        {r.name.replace("Ourisman ", "")}
                      </Link>
                      <div className="text-[11px] text-slate-500">{r.brand} · {r.city}, {r.state}</div>
                    </td>
                    <td className="py-2.5 text-right tabular font-semibold">{r.units_retailed_90d}</td>
                    <td className="py-2.5 text-right tabular font-bold">${r.avg_front_gross.toLocaleString()}</td>
                    <td className={cn("py-2.5 text-right tabular font-semibold", r.avg_days_to_sell <= 35 ? "text-verdict-strongText" : r.avg_days_to_sell <= 50 ? "text-verdict-conditionalText" : "text-verdict-passText")}>
                      {r.avg_days_to_sell}d
                    </td>
                    <td className={cn("py-2.5 text-right tabular font-semibold", r.cost_to_market_avg <= 94 ? "text-verdict-strongText" : r.cost_to_market_avg <= 98 ? "text-verdict-conditionalText" : "text-verdict-passText")}>
                      {r.cost_to_market_avg}%
                    </td>
                    <td className="py-2.5 text-right tabular font-bold text-freddy-red">{r.strong_buys_open}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="text-lg font-bold text-navy mb-4">Acquisition mix</h2>
          <div className="h-48" data-testid="acquisition-mix-chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" innerRadius={55} outerRadius={75} paddingAngle={2}>
                  {pieData.map((p) => (
                    <Cell key={p.src} fill={MIX_COLORS[p.src] || "#94A3B8"} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-4">
            {pieData.map((p) => (
              <div key={p.src} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm" style={{ background: MIX_COLORS[p.src] }} />
                  <span className="font-medium text-navy">{p.name}</span>
                </div>
                <div className="tabular font-semibold">
                  {p.value} · <span className="text-verdict-strongText">${p.gross.toLocaleString()} avg</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
        <div className="xl:col-span-2 bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-navy">Buyer scorecard</h2>
            <Link to="/corporate/buyers" className="text-xs font-semibold text-freddy-red hover:underline flex items-center gap-1">
              All buyers <ChevronRight size={12} />
            </Link>
          </div>
          <table className="w-full text-sm" data-testid="buyer-scorecard">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left py-2 font-semibold">Buyer</th>
                <th className="text-left py-2 font-semibold">Rooftop</th>
                <th className="text-right py-2 font-semibold">Units</th>
                <th className="text-right py-2 font-semibold">Hit rate</th>
                <th className="text-right py-2 font-semibold">Override</th>
              </tr>
            </thead>
            <tbody>
              {scorecard.slice(0, 8).map((b) => (
                <tr key={b.buyer_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2.5 font-semibold text-navy">{b.buyer_name}</td>
                  <td className="py-2.5 text-slate-600 text-xs">{b.rooftop_name?.replace("Ourisman ", "")}</td>
                  <td className="py-2.5 text-right tabular font-semibold">{b.units}</td>
                  <td className={cn("py-2.5 text-right tabular font-semibold", b.strong_buy_hit_rate >= 75 ? "text-verdict-strongText" : b.strong_buy_hit_rate >= 65 ? "text-slate-600" : "text-verdict-passText")}>
                    {b.strong_buy_hit_rate}%
                  </td>
                  <td className={cn("py-2.5 text-right tabular font-semibold", b.override_rate <= 15 ? "text-verdict-strongText" : b.override_rate <= 25 ? "text-verdict-conditionalText" : "text-verdict-passText")}>
                    {b.override_rate}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {coachFlags.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-freddy-red font-bold mb-2">
                <AlertTriangle size={12} /> Coaching flags
              </div>
              <div className="flex flex-wrap gap-2">
                {coachFlags.map((b) => (
                  <span key={b.buyer_id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-freddy-red/10 text-freddy-red text-[11px] font-semibold">
                    {b.buyer_name} · {b.override_rate > 25 ? `${b.override_rate}% overrides` : `${b.strong_buy_hit_rate}% hit rate`}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-navy">Opportunity alerts</h2>
            <Link to="/corporate/alerts" className="text-xs font-semibold text-freddy-red hover:underline">All</Link>
          </div>
          <div className="space-y-3" data-testid="opportunity-alerts">
            {alerts.map((a) => {
              const toneMap = { high: "border-freddy-red bg-freddy-red/5 text-freddy-red", medium: "border-verdict-conditional bg-verdict-conditional/10 text-verdict-conditionalText", low: "border-verdict-buy bg-verdict-buy/10 text-verdict-buyText" };
              const IconMap = { high: AlertTriangle, medium: Clock, low: TrendingUp };
              const Icon = IconMap[a.severity] || AlertTriangle;
              return (
                <div key={a.alert_id} className={cn("rounded-md border-l-4 p-3", toneMap[a.severity])}>
                  <div className="flex items-start gap-2">
                    <Icon size={14} className="mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-navy truncate">{a.title}</div>
                      <div className="text-xs text-slate-600 mt-0.5">{a.message}</div>
                    </div>
                  </div>
                </div>
              );
            })}
            {alerts.length === 0 && <div className="text-sm text-slate-500">No active alerts.</div>}
          </div>
        </div>
      </div>
    </Layout>
  );
}
